import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import TeacherAvatar from "components/TeacherAvatar";
import { figmaTokens } from "assets/theme/designTokens";
import { useTranslate } from "utils/i18n";

function formatShortDate(value) {
  if (!value) return "—";
  const s = String(value).replace("T", " ");
  return s.slice(0, 16);
}

function KpiTile({ label, value, accent }) {
  return (
    <SoftBox
      sx={{
        flex: "1 1 72px",
        minWidth: 72,
        px: 1.1,
        py: 1,
        borderRadius: "12px",
        border: `1px solid ${accent ? "rgba(102, 126, 234, 0.35)" : figmaTokens.borderSubtle}`,
        background: accent
          ? "linear-gradient(145deg, rgba(102, 126, 234, 0.12) 0%, rgba(255,255,255,0.9) 70%)"
          : figmaTokens.surface,
      }}
    >
      <SoftTypography
        variant="caption"
        display="block"
        sx={{ color: figmaTokens.textMuted, fontWeight: 600, fontSize: "0.65rem", letterSpacing: "0.02em" }}
      >
        {label}
      </SoftTypography>
      <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.25, lineHeight: 1.2 }}>
        {value ?? "—"}
      </SoftTypography>
    </SoftBox>
  );
}

KpiTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  accent: PropTypes.bool,
};

function Section({ icon, title, children, tone }) {
  const border =
    tone === "warning"
      ? "rgba(240, 177, 0, 0.45)"
      : tone === "success"
        ? "rgba(16, 185, 129, 0.4)"
        : figmaTokens.accent;
  return (
    <SoftBox
      sx={{
        px: 1.25,
        py: 1.1,
        borderRadius: "12px",
        bgcolor: figmaTokens.surface,
        border: `1px solid ${figmaTokens.borderSubtle}`,
        borderLeft: `3px solid ${border}`,
      }}
    >
      <SoftBox display="flex" alignItems="center" gap={0.75} mb={0.5}>
        <Icon sx={{ fontSize: "1rem !important", color: border }}>{icon}</Icon>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: figmaTokens.textMuted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {title}
        </SoftTypography>
      </SoftBox>
      {children}
    </SoftBox>
  );
}

Section.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  tone: PropTypes.oneOf(["default", "warning", "success"]),
};

function MonthSpark({ months }) {
  if (!months?.length) return null;
  const values = months.map((m) => Number(m.count ?? 0));
  const max = Math.max(...values, 1);
  return (
    <SoftBox display="flex" alignItems="flex-end" gap={0.4} height={36} mt={0.75}>
      {months.slice(-6).map((m, i) => {
        const h = Math.max(4, Math.round((Number(m.count ?? 0) / max) * 32));
        return (
          <SoftBox key={`${m.label || i}`} flex={1} display="flex" flexDirection="column" alignItems="center" gap={0.25}>
            <SoftBox
              sx={{
                width: "100%",
                maxWidth: 18,
                height: h,
                borderRadius: "4px 4px 2px 2px",
                background: figmaTokens.gradientCSS,
                opacity: 0.55 + (h / 32) * 0.45,
              }}
              title={`${m.label || ""}: ${m.count ?? 0}`}
            />
            <SoftTypography variant="caption" sx={{ fontSize: "0.58rem", color: figmaTokens.textMuted, lineHeight: 1 }}>
              {String(m.label || "").slice(0, 3)}
            </SoftTypography>
          </SoftBox>
        );
      })}
    </SoftBox>
  );
}

MonthSpark.propTypes = {
  months: PropTypes.arrayOf(PropTypes.object),
};

/**
 * Rich teacher profile card for assistant chat (Feature 3).
 */
export default function TeacherProfileCard({ profile, onAction }) {
  const { language } = useTranslate();
  const fr = language !== "en";
  if (!profile?.teacher) return null;

  const t = profile.teacher;
  const summary = profile.summary || {};
  const ranking = profile.ranking || {};
  const assignment = profile.assignment;
  const leave = profile.leaveSummary || {};
  const recent = Array.isArray(profile.recentAttendance) ? profile.recentAttendance.slice(0, 3) : [];
  const months = profile.charts?.attendanceByMonth || [];

  const radiusPct =
    summary.attendanceWithinRadiusPercent != null
      ? `${Number(summary.attendanceWithinRadiusPercent).toFixed(0)}%`
      : "—";

  const pendingCount = leave.pending?.length ?? 0;
  const leaveTone = pendingCount > 0 ? "warning" : "default";
  const leaveDetail = (() => {
    const active = leave.pending?.[0] || leave.approved?.[0] || leave.recent?.[0];
    if (!active) return fr ? "Aucune demande de congé" : "No leave requests";
    return [
      active.leaveType || active.LeaveType,
      active.status || active.Status,
      `${String(active.startDate || active.StartDate || "").slice(0, 10)} → ${String(active.endDate || active.EndDate || "").slice(0, 10)}`,
    ]
      .filter(Boolean)
      .join(" · ");
  })();

  const assignmentLabel = assignment?.estabName
    ? [assignment.estabName, assignment.locationLabel || assignment.provinceName, assignment.departmentName]
        .filter(Boolean)
        .join(" · ")
    : fr
      ? "Non affectée"
      : "Not assigned";

  const badges = [
    {
      key: "license",
      label: t.isLicensed ? (fr ? "Licenciée" : "Licensed") : fr ? "Non licenciée" : "Unlicensed",
      ok: Boolean(t.isLicensed),
    },
    t.isAllocated != null
      ? {
          key: "alloc",
          label: t.isAllocated ? (fr ? "Affectée" : "Assigned") : fr ? "Non affectée" : "Unassigned",
          ok: Boolean(t.isAllocated || assignment?.estabName),
        }
      : null,
    t.status
      ? {
          key: "status",
          label: t.status,
          ok: true,
          neutral: true,
        }
      : null,
  ].filter(Boolean);

  return (
    <SoftBox
      mt={0.5}
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${figmaTokens.borderSubtle}`,
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
        bgcolor: figmaTokens.surface,
      }}
    >
      <SoftBox
        px={1.75}
        pt={1.75}
        pb={1.5}
        sx={{
          background: `linear-gradient(135deg, rgba(102, 126, 234, 0.16) 0%, rgba(118, 75, 162, 0.08) 45%, ${figmaTokens.pageBg} 100%)`,
          borderBottom: `1px solid ${figmaTokens.borderSubtle}`,
        }}
      >
        <SoftBox display="flex" alignItems="flex-start" gap={1.5}>
          <TeacherAvatar
            teacher={t}
            profilePic={t.profilePic}
            hasProfilePic={t.hasProfilePic}
            size="lg"
            preview
            sx={{
              width: 64,
              height: 64,
              border: "3px solid #fff",
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.15)",
            }}
          />
          <SoftBox flex={1} minWidth={0}>
            <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, lineHeight: 1.25 }}>
              {t.name}
            </SoftTypography>
            <SoftTypography variant="caption" display="block" sx={{ color: figmaTokens.textSecondary, mt: 0.25 }}>
              {[t.reg, t.degree, t.phone && t.phone !== "—" ? t.phone : null].filter(Boolean).join(" · ")}
            </SoftTypography>
            <SoftBox display="flex" flexWrap="wrap" gap={0.5} mt={1}>
              {badges.map((b) => (
                <Chip
                  key={b.key}
                  size="small"
                  label={b.label}
                  sx={{
                    height: 22,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: b.neutral
                      ? "rgba(15, 23, 42, 0.06)"
                      : b.ok
                        ? "rgba(16, 185, 129, 0.12)"
                        : "rgba(239, 68, 68, 0.1)",
                    color: b.neutral ? figmaTokens.textSecondary : b.ok ? "#047857" : "#b91c1c",
                    border: "1px solid transparent",
                  }}
                />
              ))}
              {t.score != null && (
                <Chip
                  size="small"
                  icon={<Icon sx={{ fontSize: "0.85rem !important" }}>star</Icon>}
                  label={`${fr ? "Score" : "Score"} ${t.score}`}
                  sx={{
                    height: 22,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: "rgba(102, 126, 234, 0.12)",
                    color: figmaTokens.accent,
                    "& .MuiChip-icon": { color: figmaTokens.accent },
                  }}
                />
              )}
            </SoftBox>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <SoftBox px={1.5} py={1.25} display="flex" flexWrap="wrap" gap={0.75}>
        <KpiTile label={fr ? "Score" : "Score"} value={t.score ?? "—"} accent />
        <KpiTile label={fr ? "Ce mois" : "This month"} value={summary.thisMonth ?? "—"} />
        <KpiTile label={fr ? "Dans rayon" : "In radius"} value={radiusPct} />
        <KpiTile
          label={fr ? "Rang" : "Rank"}
          value={ranking.overallRank != null ? `#${ranking.overallRank}` : "—"}
        />
        <KpiTile
          label={fr ? "Dernier" : "Last seen"}
          value={
            summary.daysSinceLastAttendance != null
              ? fr
                ? `${summary.daysSinceLastAttendance}j`
                : `${summary.daysSinceLastAttendance}d`
              : "—"
          }
        />
      </SoftBox>

      <SoftBox px={1.5} pb={1.5} display="flex" flexDirection="column" gap={0.85}>
        <Section icon="place" title={fr ? "Affectation" : "Assignment"} tone={assignment?.estabName ? "success" : "default"}>
          <SoftTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.8rem", color: figmaTokens.textPrimary }}>
            {assignmentLabel}
          </SoftTypography>
          {assignment?.assignedDate && (
            <SoftTypography variant="caption" display="block" sx={{ color: figmaTokens.textMuted, mt: 0.25 }}>
              {fr ? "Depuis" : "Since"} {String(assignment.assignedDate).slice(0, 10)}
            </SoftTypography>
          )}
        </Section>

        <Section icon="event_busy" title={fr ? "Congé" : "Leave"} tone={leaveTone}>
          <SoftTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.8rem", color: figmaTokens.textPrimary }}>
            {leaveDetail}
          </SoftTypography>
          {pendingCount > 0 && (
            <SoftTypography variant="caption" display="block" sx={{ color: "#b45309", mt: 0.25, fontWeight: 700 }}>
              {fr ? `${pendingCount} en attente` : `${pendingCount} pending`}
            </SoftTypography>
          )}
        </Section>

        <Section icon="fact_check" title={fr ? "Présence" : "Attendance"}>
          <SoftTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.8rem", color: figmaTokens.textPrimary }}>
            {[
              summary.totalAttendance != null
                ? `${summary.totalAttendance} ${fr ? "pointages" : "check-ins"}`
                : null,
              summary.thisWeek != null ? `${summary.thisWeek} ${fr ? "cette semaine" : "this week"}` : null,
              summary.uniqueEstablishments != null
                ? `${summary.uniqueEstablishments} ${fr ? "établissements" : "schools"}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ") || (fr ? "Pas de données" : "No data")}
          </SoftTypography>
          <MonthSpark months={months} />
        </Section>

        {(ranking.provinceRank != null || ranking.departmentRank != null) && (
          <Section icon="leaderboard" title={fr ? "Classement" : "Ranking"}>
            <SoftTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.8rem", color: figmaTokens.textPrimary }}>
              {[
                ranking.overallRank != null ? `#${ranking.overallRank} ${fr ? "global" : "overall"}` : null,
                ranking.provinceRank != null
                  ? `#${ranking.provinceRank} ${ranking.provinceName || (fr ? "province" : "province")}`
                  : null,
                ranking.departmentRank != null
                  ? `#${ranking.departmentRank} ${ranking.departmentName || (fr ? "département" : "department")}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </SoftTypography>
          </Section>
        )}

        {recent.length > 0 && (
          <Section icon="history" title={fr ? "Derniers pointages" : "Recent check-ins"}>
            <SoftBox display="flex" flexDirection="column" gap={0.6}>
              {recent.map((row, idx) => (
                <SoftBox
                  key={row.attendanceId || `${row.date}-${idx}`}
                  display="flex"
                  justifyContent="space-between"
                  gap={1}
                  alignItems="flex-start"
                >
                  <SoftBox minWidth={0}>
                    <SoftTypography variant="caption" fontWeight="bold" display="block" sx={{ color: figmaTokens.textPrimary }}>
                      {row.estabName || "—"}
                    </SoftTypography>
                    <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                      {formatShortDate(row.date || row.time)}
                      {row.time && row.date ? "" : ""}
                    </SoftTypography>
                  </SoftBox>
                  <Chip
                    size="small"
                    label={
                      row.isWithinRadius === false
                        ? fr
                          ? "Hors rayon"
                          : "Out"
                        : fr
                          ? "OK"
                          : "In"
                    }
                    sx={{
                      height: 20,
                      fontSize: "0.6rem",
                      fontWeight: 700,
                      flexShrink: 0,
                      bgcolor: row.isWithinRadius === false ? "rgba(239, 68, 68, 0.1)" : "rgba(16, 185, 129, 0.12)",
                      color: row.isWithinRadius === false ? "#b91c1c" : "#047857",
                    }}
                  />
                </SoftBox>
              ))}
            </SoftBox>
          </Section>
        )}
      </SoftBox>

      {onAction && t.id && (
        <SoftBox
          px={1.5}
          py={1.25}
          display="flex"
          flexWrap="wrap"
          gap={0.65}
          sx={{ borderTop: `1px solid ${figmaTokens.borderSubtle}`, bgcolor: figmaTokens.pageBg }}
        >
          {[
            {
              id: "profile",
              label: fr ? "Fiche complète" : "Full profile",
              route: `/enseignant/${t.id}`,
              primary: true,
            },
            {
              id: "history",
              label: fr ? "Historique" : "History",
              route: `/histoire-des-enseignants/${t.id}`,
            },
            {
              id: "map",
              label: fr ? "Carte" : "Map",
              route: `/carte-enseignants?teacherId=${t.id}`,
            },
          ].map((act) => (
            <Chip
              key={act.id}
              size="small"
              label={act.label}
              onClick={() => onAction({ id: act.id, label: act.label, route: act.route, variant: act.primary ? "primary" : "secondary" })}
              sx={{
                height: 28,
                fontWeight: 700,
                fontSize: "0.7rem",
                ...(act.primary
                  ? {
                      background: figmaTokens.gradientCSS,
                      color: "#fff",
                      border: "none",
                    }
                  : {
                      bgcolor: figmaTokens.surface,
                      border: `1px solid ${figmaTokens.borderSubtle}`,
                    }),
              }}
            />
          ))}
        </SoftBox>
      )}
    </SoftBox>
  );
}

TeacherProfileCard.propTypes = {
  profile: PropTypes.shape({
    teacher: PropTypes.object,
    summary: PropTypes.object,
    ranking: PropTypes.object,
    assignment: PropTypes.object,
    leaveSummary: PropTypes.object,
    recentAttendance: PropTypes.array,
    charts: PropTypes.object,
  }),
  onAction: PropTypes.func,
};
